import { type FC, useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, Button } from 'shared/ui'
import { LINK_CHATBOT_GUIDELINES } from 'shared/constants/links'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useChatbotContext, useChatbotDialogContext } from 'pages/chatbot/context'
import { CreateChatbotStore } from 'pages/chatbot/store/chatbotCreateStore'
import styles from './styles.module.scss'

export const ChatbotHelpGuideButton = () => (
  <Button
    tag='a'
    text='Help guide'
    size='medium'
    contained='secondary'
    href={LINK_CHATBOT_GUIDELINES}
    target='_blank'
    rel='noreferrer'
    style={{ textDecoration: 'none' }}
    iconProps={{ icon: 'info' }}
  />
)

export const ChatbotNewButton = observer(() => {
  const { pathname } = useLocation()
  const type = pathname.includes('calling') ? 'calling' : 'texting'
  const entities = useEntitiesContext()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)

  const handleClick = async () => {
    setLoading(true)
    await new CreateChatbotStore(entities, type).create()
    setLoading(false)
  }

  useEffect(() => {
    const showCreateModal = searchParams.get('showCreateModal')

    if (showCreateModal === 'true') handleClick()
  }, [searchParams])

  return (
    <Button
      text={'New AI Agent'}
      iconProps={{ icon: 'add' }}
      size='medium'
      onClick={handleClick}
      loading={loading}
    />
  )
})

type IChatbotTestNewButtonProps = {
  className?: string
}

export const ChatbotTestNewButton: FC<IChatbotTestNewButtonProps> = observer(({ className }) => {
  const chatbot = useChatbotContext()

  const buttonDisabled = chatbot.locked || !chatbot.dirty
  const handleTestChatbot = async () => await chatbot.handleTestNewAgent()

  return (
    <Button
      className={className}
      size='medium'
      contained='secondary'
      text='Test AI Agent'
      disabled={buttonDisabled}
      onClick={handleTestChatbot}
    />
  )
})

type IChatbotTestButtonProps = {
  className?: string
  size?: 'medium' | 'small'
}

export const ChatbotTestButton: FC<IChatbotTestButtonProps> = observer(
  ({ className, size = 'medium' }) => {
    const [updating, setUpdating] = useState(false)
    const chatbot = useChatbotContext()
    const dialog = useChatbotDialogContext()
    const { firstMessage } = chatbot.model

    const buttonText = dialog.started || dialog.loading ? 'Restart AI Agent' : 'Test AI Agent'
    const buttonDisabled = updating || dialog.loading || chatbot.locked

    const handleSessionTrigger = async () => {
      if (chatbot.dirty) {
        setUpdating(true)
        const updated = await chatbot.updateChatbot(true)
        setUpdating(false)

        if (!updated) return
      }

      if (dialog.started) dialog.restart(firstMessage.isAutoConvType)
      else dialog.start(firstMessage.isAutoConvType)
    }

    return (
      <Button
        className={className}
        size={size}
        contained='secondary'
        text={buttonText}
        disabled={buttonDisabled}
        onClick={handleSessionTrigger}
      />
    )
  }
)

export const AgentTestFollowupButton: FC = observer(() => {
  const chatbot = useChatbotContext()
  const dialog = useChatbotDialogContext()
  const buttonDisabled = dialog.loading || chatbot.locked

  const handleTestFollowup = async () => {
    await dialog.testFollowup()
  }

  return (
    <Button
      size={'small'}
      contained='secondary'
      text='Test follow-up'
      disabled={buttonDisabled}
      onClick={handleTestFollowup}
    />
  )
})

type IAgentSaveDraftButtonProps = {
  handleSaveDraft: () => void
  disabled?: boolean
}

export const AgentSaveDraftButton: FC<IAgentSaveDraftButtonProps> = observer(
  ({ handleSaveDraft, disabled }) => {
    return (
      <Button
        contained='secondary'
        text='Save draft'
        onClick={handleSaveDraft}
        disabled={disabled}
      />
    )
  }
)

type IAgentFooterButtonsProps = {
  decreaseStep?: () => void
  handleNextClick?: () => void
  className?: string
  isNextButtonDisabled?: boolean
  isFirstStep?: boolean
  isLastStep?: boolean
}

export const AgentFooterButtons: FC<IAgentFooterButtonsProps> = observer(
  ({ decreaseStep, handleNextClick, className, isNextButtonDisabled, isFirstStep, isLastStep }) => {
    return (
      <div className={classNames(styles.footer, className)}>
        <Box display={'flex'} gap={1} width={'100%'}>
          {!isFirstStep && (
            <Button
              contained='secondary'
              text='Back'
              fullWidth
              iconProps={{ icon: 'chevronLeft' }}
              onClick={decreaseStep}
              className={styles.footerButton}
            />
          )}
          {!isLastStep && (
            <Button
              fullWidth
              contained='primary'
              text='Next'
              onClick={handleNextClick}
              className={styles.footerButton}
              disabled={isNextButtonDisabled}
              iconRightProps={{
                icon: 'chevronRight',
                color: 'var(--white-100)',
                className: styles.selectItemIcon,
              }}
            />
          )}
        </Box>
      </div>
    )
  }
)

type IAgentNewTestButtonProps = {
  className?: string
}

export const AgentNewTestButton: FC<IAgentNewTestButtonProps> = observer(({ className }) => {
  const {
    agentBookAppointmentStepsStore: { saveAgentBeforeTest, stepLoading },
  } = useChatbotContext()

  const handleTest = async () => {
    await saveAgentBeforeTest()
  }

  return (
    <Button
      className={className}
      size='medium'
      contained='secondary'
      text='Test your Agent'
      onClick={handleTest}
      disabled={stepLoading}
    />
  )
})

type IAgentTestButtonProps = {
  className?: string
  size?: 'medium' | 'small'
}

export const AgentTestButton: FC<IAgentTestButtonProps> = observer(
  ({ className, size = 'medium' }) => {
    const {
      agentBookAppointmentStepsStore: {
        settingAgentStore,
        isExistChanges,
        stepLoading,
        saveAgentBeforeTest,
      },
      id,
      locked,
    } = useChatbotContext()
    const dialog = useChatbotDialogContext()
    const { isAutoConvType, checkValidate } = settingAgentStore
    const isActiveState = dialog.started || dialog.loading

    const buttonText = isActiveState ? 'Restart Agent' : 'Test your Agent'
    const buttonDisabled = stepLoading || dialog.loading || locked

    const handleSessionTrigger = async () => {
      if (isExistChanges) {
        const isSaved = await saveAgentBeforeTest({ id: Number(id) })
        if (!isSaved) return
      }

      if (!checkValidate()) return

      if (dialog.started) dialog.restart(isAutoConvType)
      else dialog.start(isAutoConvType)
    }

    return (
      <Button
        className={className}
        size={size}
        contained={'secondary'}
        text={buttonText}
        disabled={buttonDisabled}
        onClick={handleSessionTrigger}
      />
    )
  }
)
